/**
 * Pure form state + API patch helpers for Store Settings.
 * Keeps StoreSettings-Page.jsx thin; same behavior as before extraction.
 */

import { PICKUP_FREE_RESERVED_ROW, COMBO_MENU_LABEL_OPTIONS } from './storeSettingsConstants';
import { capitalizeWords } from './useStoreSettings';

export const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

/**
 * Normalizes any supported time text to `HH:MM AM/PM`.
 * Returns empty string when value is not parseable as a 12-hour time.
 */
/**
 * API shape for `restrictedDeliveryDay`: `null` when no weekday restriction (UI "None" / empty).
 * Weekdays are sent lowercase to match `WEEKDAY_OPTIONS`.
 */
const restrictedDeliveryDayToApi = (value) => {
  const s = String(value ?? '')
    .trim()
    .toLowerCase();
  if (!s || s === 'none') return null;
  return s;
};

export const normalizeTimeTo12Hour = (value) => {
  const raw = String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ');
  if (!raw) return '';

  const match = raw.match(/^(\d{1,2}):(\d{2})\s*([aApP][mM])$/);
  if (!match) return '';

  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) return '';
  if (hour < 1 || hour > 12 || minute < 0 || minute > 59) return '';

  const meridiem = match[3].toUpperCase();
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${meridiem}`;
};

/** `store_front_images` sub-fields; API sends the string `"none"` as an unset sentinel. */
export const STORE_FRONT_IMAGE_KEYS = [
  'store_logo_url',
  'your_pick_image_one_url',
  'your_pick_image_two_url',
  'your_pick_image_three_url',
  'splash_ad_image_url',
  'store_menu_banner_image_url',
  'scanned_for_you_image_url',
];

const normalizeStoreFrontImageUrl = (value) => {
  const s = value != null ? String(value).trim() : '';
  return !s || s.toLowerCase() === 'none' ? '' : s;
};

const getEmptyStoreFrontImages = () =>
  STORE_FRONT_IMAGE_KEYS.reduce((acc, key) => ({ ...acc, [key]: '' }), {});

const serializeStoreFrontImages = (images) =>
  JSON.stringify(
    STORE_FRONT_IMAGE_KEYS.reduce((acc, key) => ({ ...acc, [key]: images?.[key] || '' }), {})
  );

const isPickupFreeReservedRow = (row) =>
  String(row?.name ?? '')
    .trim()
    .toLowerCase() === 'none' &&
  String(row?.size ?? '')
    .trim()
    .toLowerCase() === 'none';

/**
 * First row is always the reserved `none` / `none` slot. Up to 3 additional rows may follow.
 * Strips a leading none/none from API data, keeps at most 3 user entries (no padding).
 */
export const normalizePickupFreeItems = (rawItems) => {
  const mapped = (Array.isArray(rawItems) ? rawItems : []).map((i) => ({
    name: i?.name != null ? String(i.name) : '',
    size: i?.size != null ? String(i.size) : '',
  }));

  let userItems = [...mapped];
  if (userItems.length > 0 && isPickupFreeReservedRow(userItems[0])) {
    userItems = userItems.slice(1);
  }
  userItems = userItems.slice(0, 3);

  return [{ ...PICKUP_FREE_RESERVED_ROW }, ...userItems];
};

export const getEmptyFormState = () => ({
  kitchen_name: '',
  phone_no: '',
  display_name: '',
  display_image: '',
  store_front_images: getEmptyStoreFrontImages(),
  operation_start_time: '',
  operation_end_time: '',
  restrictedDeliveryDay: '',
  delivery_agreement_fees: {
    minimumAmountSpend: '',
    percentageToPay_byKitchen: '',
  },
  pickup_order_promotion: {
    minimumAmountSpend: '',
    pickup_promo_available: false,
    pickup_free_items: normalizePickupFreeItems([]),
  },
  comboMenuAvailable: false,
  comboMenuPrice: '',
  comboMenuLabelName: COMBO_MENU_LABEL_OPTIONS[0],
  regularMenuAvailable: false,
  search_key_words: [],
  delivery_type_available: 'both',
  delivery_kitchen_fee: '',
  promo_tracking_code: '',
  food_filter_categories: [],
  religious_filter_categories: [],
});

/** Map OnLoadKitchenInformation `data.data.data` into local form shape. */
export const mapLoadResponseToForm = (dataRoot) => {
  if (!dataRoot) return null;
  const ko = dataRoot.kitchenOperationObject || {};
  const ki = dataRoot.KitchenInfo || {};
  const promo = ko.pickup_order_promotion || {};
  const fee =
    Array.isArray(ko.delivery_agreement_fees) && ko.delivery_agreement_fees.length
      ? ko.delivery_agreement_fees[0]
      : {};

  const rawItems = Array.isArray(promo.pickup_free_items) ? promo.pickup_free_items : [];
  const pickup_free_items = normalizePickupFreeItems(rawItems);

  return {
    kitchen_name:
      ki.kitchenName != null
        ? String(ki.kitchenName)
        : ko.kitchen_name != null
          ? String(ko.kitchen_name)
          : '',
    phone_no:
      ki.phone_no != null
        ? String(ki.phone_no)
        : ko.phone_no != null
          ? String(ko.phone_no)
          : '',
    display_name: ko.display_name != null ? capitalizeWords(String(ko.display_name)) : '',
    display_image: ko.display_image != null ? String(ko.display_image) : '',
    store_front_images: STORE_FRONT_IMAGE_KEYS.reduce(
      (acc, key) => ({
        ...acc,
        [key]: normalizeStoreFrontImageUrl((ko.store_front_images || {})[key]),
      }),
      {}
    ),
    operation_start_time: normalizeTimeTo12Hour(ko.operation_start_time),
    operation_end_time: normalizeTimeTo12Hour(ko.operation_end_time),
    restrictedDeliveryDay:
      ko.restrictedDeliveryDay != null ? String(ko.restrictedDeliveryDay) : '',
    delivery_agreement_fees: {
      minimumAmountSpend:
        fee.minimumAmountSpend != null ? String(fee.minimumAmountSpend) : '',
      percentageToPay_byKitchen:
        fee.percentageToPay_byKitchen != null ? String(fee.percentageToPay_byKitchen) : '',
    },
    pickup_order_promotion: {
      minimumAmountSpend:
        promo.minimumAmountSpend != null ? String(promo.minimumAmountSpend) : '',
      pickup_promo_available: promo.pickup_promo_available === true,
      pickup_free_items,
    },
    comboMenuAvailable: ko.comboMenuAvailable === true,
    comboMenuPrice: ko.comboMenuPrice != null ? String(ko.comboMenuPrice) : '',
    comboMenuLabelName:
      ko.comboMenuLabelName != null ? String(ko.comboMenuLabelName) : COMBO_MENU_LABEL_OPTIONS[0],
    regularMenuAvailable: ko.regularMenuAvailable === true,
    search_key_words: Array.isArray(ko.search_key_words)
      ? ko.search_key_words.map((k) => (k != null ? String(k) : ''))
      : [],
    delivery_type_available:
      ko.delivery_type_available != null ? String(ko.delivery_type_available) : 'both',
    delivery_kitchen_fee:
      ko.delivery_kitchen_fee != null ? String(ko.delivery_kitchen_fee) : '',
    promo_tracking_code:
      ko.promo_tracking_code != null ? String(ko.promo_tracking_code) : '',
    food_filter_categories: Array.isArray(ko.food_filter_categories)
      ? ko.food_filter_categories.map((c) => (c != null ? String(c) : ''))
      : [],
    religious_filter_categories: Array.isArray(ko.religious_filter_categories)
      ? ko.religious_filter_categories.map((c) => (c != null ? String(c) : ''))
      : [],
  };
};

const serializeKeywords = (arr) => JSON.stringify((arr || []).map((k) => String(k)));

const serializePickupPromo = (p) =>
  JSON.stringify({
    minimumAmountSpend: String(p?.minimumAmountSpend ?? ''),
    pickup_promo_available: p?.pickup_promo_available === true,
    pickup_free_items: (p?.pickup_free_items || []).map((i) => ({
      name: String(i?.name ?? ''),
      size: String(i?.size ?? ''),
    })),
  });

export const isTabDirty = (tabId, form, baseline, options = {}) => {
  if (!form || !baseline) return false;
  const { pendingStoreImageFile, pendingStoreFrontImageFiles } = options;
  switch (tabId) {
    case '1':
      return (
        !!pendingStoreImageFile ||
        Object.values(pendingStoreFrontImageFiles || {}).some(Boolean) ||
        form.phone_no !== baseline.phone_no ||
        form.display_name !== baseline.display_name ||
        form.display_image !== baseline.display_image ||
        serializeStoreFrontImages(form.store_front_images) !==
          serializeStoreFrontImages(baseline.store_front_images)
      );
    case '2':
      return (
        form.operation_start_time !== baseline.operation_start_time ||
        form.operation_end_time !== baseline.operation_end_time ||
        restrictedDeliveryDayToApi(form.restrictedDeliveryDay) !==
          restrictedDeliveryDayToApi(baseline.restrictedDeliveryDay)
      );
    case '3':
      return (
        form.comboMenuAvailable !== baseline.comboMenuAvailable ||
        form.comboMenuPrice !== baseline.comboMenuPrice ||
        form.comboMenuLabelName !== baseline.comboMenuLabelName ||
        form.regularMenuAvailable !== baseline.regularMenuAvailable
      );
    case '4':
      return (
        form.delivery_agreement_fees.minimumAmountSpend !==
          baseline.delivery_agreement_fees.minimumAmountSpend ||
        form.delivery_agreement_fees.percentageToPay_byKitchen !==
          baseline.delivery_agreement_fees.percentageToPay_byKitchen
      );
    case '5':
      return (
        serializePickupPromo(form.pickup_order_promotion) !==
        serializePickupPromo(baseline.pickup_order_promotion)
      );
    case '6':
      return (
        form.delivery_type_available !== baseline.delivery_type_available ||
        form.delivery_kitchen_fee !== baseline.delivery_kitchen_fee ||
        form.promo_tracking_code !== baseline.promo_tracking_code
      );
    case '7':
      return serializeKeywords(form.search_key_words) !== serializeKeywords(baseline.search_key_words);
    case '8':
      return (
        serializeKeywords(form.food_filter_categories) !==
          serializeKeywords(baseline.food_filter_categories) ||
        serializeKeywords(form.religious_filter_categories) !==
          serializeKeywords(baseline.religious_filter_categories)
      );
    default:
      return false;
  }
};

/**
 * Partial body for PUT `updateKitchenOperationAPI` — only keys that changed vs `baseline`.
 * `delivery_agreement_fees` is always a one-element array when the agreement tab changes.
 * `kitchen_name` is not included here; add it at send time (JWT + fallback) in `onKitchenOperationUpdateAPI`.
 */
export const buildTabUpdatePayload = (tabId, form, baseline) => {
  if (!form || !baseline) return {};
  const patch = {};

  const addKw = () => {
    patch.search_key_words = form.search_key_words
      .map((k) => String(k).trim())
      .filter((k) => k !== '');
  };

  switch (tabId) {
    case '1':
      if (form.phone_no !== baseline.phone_no) patch.phone_no = form.phone_no;
      if (form.display_name !== baseline.display_name) patch.display_name = form.display_name;
      if (form.display_image !== baseline.display_image) patch.display_image = form.display_image;
      if (
        serializeStoreFrontImages(form.store_front_images) !==
        serializeStoreFrontImages(baseline.store_front_images)
      )
        patch.store_front_images = { ...form.store_front_images };
      break;
    case '2':
      if (form.operation_start_time !== baseline.operation_start_time)
        patch.operation_start_time = normalizeTimeTo12Hour(form.operation_start_time);
      if (form.operation_end_time !== baseline.operation_end_time)
        patch.operation_end_time = normalizeTimeTo12Hour(form.operation_end_time);
      if (
        restrictedDeliveryDayToApi(form.restrictedDeliveryDay) !==
        restrictedDeliveryDayToApi(baseline.restrictedDeliveryDay)
      )
        patch.restrictedDeliveryDay = restrictedDeliveryDayToApi(form.restrictedDeliveryDay);
      break;
    case '3':
      if (form.comboMenuAvailable !== baseline.comboMenuAvailable)
        patch.comboMenuAvailable = form.comboMenuAvailable;
      if (form.comboMenuPrice !== baseline.comboMenuPrice)
        patch.comboMenuPrice = Number(form.comboMenuPrice) || 0;
      if (form.comboMenuLabelName !== baseline.comboMenuLabelName)
        patch.comboMenuLabelName = form.comboMenuLabelName;
      if (form.regularMenuAvailable !== baseline.regularMenuAvailable)
        patch.regularMenuAvailable = form.regularMenuAvailable;
      break;
    case '4':
      if (
        form.delivery_agreement_fees.minimumAmountSpend !==
          baseline.delivery_agreement_fees.minimumAmountSpend ||
        form.delivery_agreement_fees.percentageToPay_byKitchen !==
          baseline.delivery_agreement_fees.percentageToPay_byKitchen
      ) {
        patch.delivery_agreement_fees = [
          {
            minimumAmountSpend: Number(form.delivery_agreement_fees.minimumAmountSpend) || 0,
            percentageToPay_byKitchen:
              Number(form.delivery_agreement_fees.percentageToPay_byKitchen) || 0,
          },
        ];
      }
      break;
    case '5':
      if (
        serializePickupPromo(form.pickup_order_promotion) !==
        serializePickupPromo(baseline.pickup_order_promotion)
      ) {
        patch.pickup_order_promotion = {
          minimumAmountSpend: Number(form.pickup_order_promotion.minimumAmountSpend) || 0,
          pickup_promo_available: form.pickup_order_promotion.pickup_promo_available,
          pickup_free_items: form.pickup_order_promotion.pickup_free_items.map((item) => ({
            name: item.name,
            size: item.size,
          })),
        };
      }
      break;
    case '6':
      if (form.delivery_type_available !== baseline.delivery_type_available)
        patch.delivery_type_available = form.delivery_type_available;
      if (form.delivery_kitchen_fee !== baseline.delivery_kitchen_fee)
        patch.delivery_kitchen_fee = Number(form.delivery_kitchen_fee) || 0;
      if (form.promo_tracking_code !== baseline.promo_tracking_code)
        patch.promo_tracking_code = form.promo_tracking_code;
      break;
    case '7':
      if (serializeKeywords(form.search_key_words) !== serializeKeywords(baseline.search_key_words))
        addKw();
      break;
    case '8':
      if (
        serializeKeywords(form.food_filter_categories) !==
        serializeKeywords(baseline.food_filter_categories)
      )
        patch.food_filter_categories = [...form.food_filter_categories];
      if (
        serializeKeywords(form.religious_filter_categories) !==
        serializeKeywords(baseline.religious_filter_categories)
      )
        patch.religious_filter_categories = [...form.religious_filter_categories];
      break;
    default:
      break;
  }
  return patch;
};

export const mergeBaselineForTab = (baseline, tabId, form) => {
  const next = deepClone(baseline);
  switch (tabId) {
    case '1':
      next.phone_no = form.phone_no;
      next.display_name = form.display_name;
      next.display_image = form.display_image;
      next.store_front_images = deepClone(form.store_front_images);
      break;
    case '2':
      next.operation_start_time = form.operation_start_time;
      next.operation_end_time = form.operation_end_time;
      next.restrictedDeliveryDay = form.restrictedDeliveryDay;
      break;
    case '3':
      next.comboMenuAvailable = form.comboMenuAvailable;
      next.comboMenuPrice = form.comboMenuPrice;
      next.comboMenuLabelName = form.comboMenuLabelName;
      next.regularMenuAvailable = form.regularMenuAvailable;
      break;
    case '4':
      next.delivery_agreement_fees = deepClone(form.delivery_agreement_fees);
      break;
    case '5':
      next.pickup_order_promotion = deepClone(form.pickup_order_promotion);
      break;
    case '6':
      next.delivery_type_available = form.delivery_type_available;
      next.delivery_kitchen_fee = form.delivery_kitchen_fee;
      next.promo_tracking_code = form.promo_tracking_code;
      break;
    case '7':
      next.search_key_words = deepClone(form.search_key_words);
      break;
    case '8':
      next.food_filter_categories = deepClone(form.food_filter_categories);
      next.religious_filter_categories = deepClone(form.religious_filter_categories);
      break;
    default:
      break;
  }
  return next;
};

export const buildStoreSettingsPayload = (form) => ({
  kitchen_name: form.kitchen_name,
  phone_no: form.phone_no || undefined,
  display_name: form.display_name || undefined,
  display_image: form.display_image || undefined,
  store_front_images: { ...form.store_front_images },
  operation_start_time: normalizeTimeTo12Hour(form.operation_start_time) || undefined,
  operation_end_time: normalizeTimeTo12Hour(form.operation_end_time) || undefined,
  restrictedDeliveryDay: restrictedDeliveryDayToApi(form.restrictedDeliveryDay),
  delivery_agreement_fees: [
    {
      minimumAmountSpend: Number(form.delivery_agreement_fees.minimumAmountSpend) || 0,
      percentageToPay_byKitchen:
        Number(form.delivery_agreement_fees.percentageToPay_byKitchen) || 0,
    },
  ],
  pickup_order_promotion: {
    minimumAmountSpend: Number(form.pickup_order_promotion.minimumAmountSpend) || 0,
    pickup_promo_available: form.pickup_order_promotion.pickup_promo_available,
    pickup_free_items: form.pickup_order_promotion.pickup_free_items.map((item) => ({
      name: item.name,
      size: item.size,
    })),
  },
  comboMenuAvailable: form.comboMenuAvailable,
  comboMenuPrice: Number(form.comboMenuPrice) || 0,
  comboMenuLabelName: form.comboMenuLabelName,
  regularMenuAvailable: form.regularMenuAvailable,
  search_key_words: form.search_key_words.filter((k) => k.trim() !== ''),
  delivery_type_available: form.delivery_type_available,
  delivery_kitchen_fee: Number(form.delivery_kitchen_fee) || 0,
  promo_tracking_code: form.promo_tracking_code,
  food_filter_categories: [...form.food_filter_categories],
  religious_filter_categories: [...form.religious_filter_categories],
});
